/* global document, navigator, window */

import get from 'lodash/get';
import videojs from 'video.js';

import './bootstrap';

import Embed from './assets/Embed';
import { adsManagerLoadedCallback, getAdStartEvent } from './ads';
import { setQuartileEvents } from './events/quartiles';
import { evaluateSettings } from './evaluateSettings';

/**
 * Class wrapping a VideoJS (video.js) player with Hearst-specific
 * functionality.
 */
export default class Player {
  /**
   * Create a video player for playing Embed assets via the third-party
   * VideoJS player, capturing Hearst-specific semantics.
   *
   * @param {HTMLElement} el - Player element.
   * @param {Object} settings - Settings object.
   */
  constructor(el, settings) {
    this.el = el;
    this.settings = settings;
    this.evaluatedSettings = {};

    // TODO: Seems like the wrong thing to default to.
    this.player = {};
  }

  /**
   * Retrieve video embed asset data and insert video player.
   */
  load() {
    const source = this.getSource();
    const playerElementId = this.getPlayerElementId(this.getEmbedId());

    this.appendVideoElement(playerElementId);
    this.player = videojs(playerElementId);

    source
      .load()
      .then(() => {
        this.source = source;
        this.configure(this.player);
      })
      .catch(error => videojs.log.error(error));
  }

  /**
   * Configure the player. This should be overriden by subclasses
   * in order to configure asset-specific behavior.
   * @param player {Object} A VideoJS player object.
   */
  configure(player) {
    // set up evaluated settings so before doing any setup
    this.evaluatedSettings = evaluateSettings(this.el, this.source);

    this.setPlayerVolume(player);
    this.setPlayerLayout(player);
    this.setPlayerControls(player);
    this.setPlayerEvents(player);
    this.setPlayerAds(player);
    this.setPlayerSrc(player);
    this.setPlayerDock(player);
    this.setPlayerPreviewImage(player);
    this.setPlayerSharing(player);
    this.setPlayerPlaylistControls(player);
    this.setPlayerQualityControls(player);
  }

  /**
   * Sets the muted value based on the embed setting and the capability
   * of the media element itself.
   * @param {videojs} player A VideoJS player object.
   * @param {boolean} muted the value pulled from the embed itself.
   */
  setPlayerVolume(player) {
    const { volume, muted } = this.evaluatedSettings;

    // on IOS, volume control is not available, so we need to check
    // for this feature to be available before muting the player
    if (player.tech(true).featuresVolumeControl) {
      player.muted(muted);
      player.volume(volume);
    }
  }

  /**
   * Sets up the fluid/aspect-ratio-locked layout of the player.
   * @param {Object} player - A VideoJS player object.
   */
  setPlayerLayout(player) {
    const { aspectRatio } = this.evaluatedSettings;

    if (aspectRatio) {
      player.aspectRatio(aspectRatio);
    } else {
      // This seems a reasonable default for when we are not specifying
      // an aspect ratio.
      player.fluid(true);
    }
  }

  /**
   * Register player events.
   * @param player {Object} A VideoJS player instance.
   */
  setPlayerEvents(player) {
    const { imaSdkAvailable } = this.settings;

    // We only want to register basic ad events when we have the IMA SDK.
    if (imaSdkAvailable) {
      player.one(getAdStartEvent(navigator), this.adStartHandler.bind(this, player, this.source));
    }

    setQuartileEvents(player);
  }

  /**
   * Set up the dock for single video embeds
   * @param {Object} player
   */
  setPlayerDock(player) {
    if (!this.source.isPlaylist && this.evaluatedSettings.dock) {
      const { title } = this.source;
      const description = this.source.videoSource ? `by ${this.source.videoSource}` : '';

      player.dock({ title, description });
    }
  }

  /**
   * Set the preview image for single video embeds
   * @param {Object} player
   */
  setPlayerPreviewImage(player) {
    if (!this.source.isPlaylist) {
      player.poster(this.getPoster(this.source.video));
    }
  }

  /**
   * Set basic player controls
   * @param player {videojs} A VideoJS player object.
   */
  setPlayerControls(player) {
    const { controls, autoplay } = this.evaluatedSettings;

    player.controls(!!controls);
    player.autoplay(autoplay && this.settings.imaSdkAvailable);
    player.preload(false);
  }

  /**
   * Set player ad attributes that are needed for proper handling by
   * the IMA SDK.
   *
   * @param {videojs} player - A VideoJS player object.
   */
  setPlayerAds(player) {
    if (!this.settings.imaSdkAvailable) {
      return;
    }

    const imaOptions = {
      id: this.getPlayerElementId(this.source.id),
      adsManagerLoadedCallback: this.getAdManagerLoadedCallback().bind(this, player),
      adsRenderingSettings: get(this.settings, 'adSettings.adsRenderingSettings', {}),
      contribAdsSettings: get(this.settings, 'adSettings.contribAdsSettings', {}),
    };

    player.ima(imaOptions);

    // HACK: fixes a bug where the bound method for the ad UI does not
    //       exist, and thus an incorrect method gets called when looking
    //       for mouse up volume events. (https://github.com/googleads/videojs-ima/issues/568)
    /* eslint-disable */
    player.ima.controller.adUi.boundOnMouseMove = player.ima.controller.adUi.onMouseMove.bind(
      player.ima.controller.adUi,
    );
    player.ima.controller.adUi.boundOnMouseUp = player.ima.controller.adUi.onMouseUp.bind(
      player.ima.controller.adUi,
    );
    /* eslint-enable */

    // HACK: The code below prevents a bug in which following
    //       the postroll advertisement (if any), the video
    //       to be shown on the player would lose its type
    //       information, trigging an error overlay (despite
    //       the video playing as usual).

    // eslint-disable-next-line
    player.ima.controller.playerWrapper.onAllAdsCompleted = function() {
      if (this.contentComplete === true) {
        if (this.h5Player.src !== this.contentSource) {
          this.vjsPlayer.src(this.vjsPlayer.currentSource());
        }
        this.controller.onContentAndAdsCompleted();
      }
    };
  }

  /**
   * Helper method for loading ads dynamically. Lumiere and Glimmer handle ad
   * events differently.
   */
  getAdManagerLoadedCallback() {
    // eslint-disable-line class-methods-use-this
    return adsManagerLoadedCallback;
  }

  /**
   * Helper for grabbing values out of the playlist's videos for use by plugins.
   * @return {Object}
   */
  getPlaylistSrc() {
    return this.source.videos.map(video => {
      const item = {
        sources: video.src,
        thumbnail: video.thumbnail,
        title: video.title,
        description: video.videoSource ? `by ${video.videoSource}` : '',
        customPosterSetter: player_ => player_.poster(this.getPoster(video)),
      };

      const adTag = this.getAdTag(video);
      if (adTag) {
        item.customSrcSetter = (player_, item_) => {
          player_.ima.setContentWithAdTag(item_.sources, adTag, false);
          player_.ima.requestAds();
        };
      }

      return item;
    });
  }

  /**
   * Set player source for a playlist.
   */
  setPlayerPlaylistSrc(player) {
    const playlist = this.getPlaylistSrc();

    player.playlist(playlist);

    if (this.settings.enableAutoAdvance) {
      player.playlist.autoadvance(this.settings.autoAdvanceDelayInSeconds || 0);
    }
  }

  /**
   * Set the player source so that we can still load/not load the
   * player based on the presense of the IMA SDK.
   * @param {videojs} player - A VideoJS player object.
   */
  setPlayerSrc(player) {
    if (this.source.isPlaylist) {
      this.setPlayerPlaylistSrc(player);
      return;
    }

    const { src, video } = this.source;

    if (!this.source.isVideoViewable(this.source.video)) {
      player.error('This video is no longer available.');
      return;
    }

    // embeds and element settings dictate if we should
    // try to serve ads, but if the SDK is not available we
    // should not try to do it anyway.
    const adTag = this.getAdTag(video);
    if (adTag) {
      player.ima.setContentWithAdTag(src, adTag, false);
      player.ima.requestAds();
    } else {
      player.src(src);
    }
  }

  /**
   * Set up the sharing plugin.
   * @param {Object} a VideoJS player instance.
   */
  setPlayerSharing(player) {
    const shareOptions = {
      socials: ['fb', 'tw'],
      url: window.location.href || '',
      embedCode: `<div class='hearstPlayer' data-player-id='${this.source.id}' />`,

      ...this.settings.sharingOptions,
    };

    player.share(shareOptions);
  }

  /**
   * Register the playlist controls plugin.
   * @param player {Object} A VideoJS player instance.
   */
  setPlayerPlaylistControls(player) {
    // if we are not an actual playlist, we don't need these controls
    if (!this.source.isPlaylist) {
      return;
    }

    const defaultOptions = {
      videos: this.getPlaylistSrc(),
    };

    player.playlistControls(defaultOptions);
    player.playlistOverlay(defaultOptions);
  }

  /**
   * Register the player quality controls plugin
   * @param player {Object} A VideoJS player instance.
   */
  setPlayerQualityControls(player) {
    if (this.settings.enableQualityLevels) {
      player.qualityLevels();
      player.hlsQualitySelector();
    }
  }

  /**
   * Ad start event handler.
   * @param {Object} source - The source asset.
   * @param {Object} settings - Player settings.
   */
  adStartHandler(player) {
    player.ima.initializeAdDisplayContainer();
  }

  /**
   * Append video element into player element.
   * @param {string} ud - The HTNML ID of the video element.
   * @param {Function} callback - Callback executed after setting attributes.
   */
  appendVideoElement(id, callback = false) {
    const videoEl = document.createElement('video');

    videoEl.setAttribute('id', id);
    videoEl.setAttribute('class', 'video-js vjs-default-skin vjs-big-play-centered');
    videoEl.setAttribute('playsinline', null);

    if (callback) {
      callback.apply(videoEl);
    }

    this.el.appendChild(videoEl);
  }

  /**
   * Helper method, override in Glimmer to handle more complex image resizing
   * @param {Video} video
   */
  getPoster(video) {
    return video.croppedPreviewImage;
  }

  // eslint-disable-next-line no-unused-vars
  getAdTag(video) {
    const { ads, adTag } = this.evaluatedSettings;

    // no matter what, if we dont have the SDK or ads are disabled,
    // return nothing
    if (!this.settings.imaSdkAvailable || !ads) {
      return '';
    }

    return adTag || this.source.adTag;
  }

  /*
   * The embed ID of the the asset this player should load and
   * display.
   */
  getEmbedId() {
    return this.el.getAttribute('data-embed-id') || this.el.getAttribute('data-player-id');
  }

  /**
   * The source (asset) this player should display, with the
   * attributes needed for the player to function.
   * @return {Object} An Embed object.
   */
  getSource() {
    return new Embed(this.getEmbedId(), this.settings);
  }

  /**
   * Returns the ID of the root player DOM element.
   * @return {string}
   */
  getPlayerElementId(id) {
    return `${this.settings.playerClass}-${id}`;
  }
}



// WEBPACK FOOTER //
// ./node_modules/video-js-core/src/Player.js